import * as Yup from 'yup'
import { getEmaiValidation, getRequired } from './utils'

const required = Yup.string().required(getRequired)
const emailValidate = (req = true) => {
  let schema = Yup.string().matches(/^[^\s@]+@[^\s@]+\.[^\s@]+\w+$/, getEmaiValidation)
  if (req) schema = schema.required(getRequired)
  return schema
}

const passwordValidate = (req) => {
  return Yup.string()
    .min(8)
    .matches(/[A-Z]/)
    .matches(/[a-z]/)
    .matches(/[0-9]/)
    .matches(/[!+@#$-%]/)
    .required(req)
}

const validatePassword = (password) => {
  return (
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[@!#$%^&*()\-_]/.test(password) &&
    password.length >= 8
  )
}

export const LoginSchema = Yup.object({
  username: required,
  password: required,
})

export const newsLetterSchema = Yup.object({
  firstName: required,
  lastName: required,
  email: emailValidate(),
})

export const submitIntakeSchema = Yup.object({
  emailAddress: emailValidate(),
})

export const ForgotUsernameSchema = Yup.object({
  email: emailValidate(),
})

export const ForgotPasswordSchema = Yup.object({
  username: required,
})
export const getStrength = (password, messages, isUserName = false) => {
  if (!password) {
    return { width: '0%', message: '', className: 'bg-light' }
  }
  if (password.length < 8) {
    return {
      width: '25%',
      message: messages?.txt_tooshort || messages?.txt_toshort,
      className: 'progress-bar progress-bar-danger',
    }
  }
  if (!validatePassword(password) && !isUserName) {
    return {
      width: '50%',
      message: messages?.txt_bad,
      className: 'progress-bar progress-bar-danger',
    }
  }
  if ((validatePassword(password) || isUserName) && password.length === 8) {
    return {
      width: '75%',
      message: messages?.txt_good,
      className: 'progress-bar progress-bar-warning',
    }
  }
  if ((validatePassword(password) || isUserName) && password.length > 8) {
    return {
      width: '100%',
      message: messages?.txt_strong,
      className: 'progress-bar progress-bar-success',
    }
  }
}

export const ResetPasswordSchema = (mismatchpassword) => {
  return Yup.object().shape({
    password: passwordValidate(getRequired),
    confirmPassword: Yup.string()
      .required(getRequired)
      .oneOf([Yup.ref('password'), null], mismatchpassword),
  })
}

export const FeedbackSchema = Yup.object({
  FeedbackSource: required,
  Feedbackoption: Yup.string()
    .nullable()
    .when('FeedbackSource', {
      is: (val) => val === '3',
      then: () => required,
      otherwise: () => Yup.string().nullable(),
    }),
  Feedbackoptionother: Yup.string()
    .nullable()
    .when('FeedbackSource', {
      is: '4',
      then: () => required,
    }),
})

export const ReferClientValidation = Yup.object({
  firstname: required,
  lastname: required,
  emailaddress: emailValidate(),
})

export const attorneySearchSchema = Yup.object().shape({
  fromDate: Yup.object().shape({
    day: Yup.number().test(
      'valid-day',
      'Invalid date: Please choose a valid day for the selected month and year.',
      function (value) {
        const { month } = this.parent
        const daysInMonth = new Date(this.parent.year, month, 0).getDate()
        return value <= daysInMonth
      },
    ),
  }),
  toDate: Yup.object().shape({
    day: Yup.number().test(
      'valid-day',
      'Invalid date:  Please choose a valid day for the selected month and year.',
      function (value) {
        const { month } = this.parent
        const daysInMonth = new Date(this.parent.year, month, 0).getDate()
        return value <= daysInMonth
      },
    ),
  }),
})
export const uploadMapSchema = Yup.object({
  firstName: required,
  lastName: required,
  email: emailValidate(false),
  fileDetails: Yup.mixed().required(getRequired),
})

export const inviteClientSchema = (formType) => {
  return Yup.object().shape({
    firstName: required,
    lastName: required,
    email: Yup.string().email(getEmaiValidation).required(getRequired),
    ...(formType === 'allied' && {
      refAttorneyEmail: Yup.string().email(getEmaiValidation).required(getRequired),
    }),
  })
}

export const addMapToAttorneySchema = Yup.object({
  email: Yup.string().email(getEmaiValidation).required(getRequired),
})

const childQuestionSchema = Yup.array()
  .nullable(true)
  .when('selectedchoice', {
    is: (val) => val !== null,
    then: () =>
      Yup.array()
        .nullable(true)
        .of(
          Yup.object().shape({
            is_skipquestion: Yup.boolean(),
            answer: answerSchema,
            choice1: choice1Schema,
          }),
        ),
    otherwise: (schema) => schema.nullable(true),
  })

const choice1Schema = Yup.array()
  .nullable(true)
  .of(
    Yup.object({
      recordid: Yup.string(),
      selectedchoice: Yup.string().nullable(true),
      childQuestion: childQuestionSchema,
    }),
  )

const answerSchema = Yup.object().when('is_skipquestion', {
  is: (is_skipquestion) => !is_skipquestion,
  then: () =>
    Yup.object().shape({
      answer1: Yup.string().when('ddl_questiontype', {
        is: 6,
        then: (schema) => schema.nullable(true),
        otherwise: (schema) => schema.required(getRequired),
      }),
      answer2: Yup.string().when(['answer1', 'ddl_questiontype'], {
        is: (answer1, ddlType) => {
          return (answer1 !== '0' && ddlType === 11) || [20, 21].includes(ddlType)
        },
        then: (schema) => schema.required(getRequired),
        otherwise: (schema) => schema.nullable(true),
      }),
      answer3: Yup.string().when(['answer1', 'answer2', 'ddl_questiontype', 'statesLength'], {
        is: (answer1, answer2, ddlType, statesLength) => {
          return (
            (answer1 !== '0' &&
              !['0', ''].includes(answer2) &&
              ddlType === 11 &&
              statesLength > 1) ||
            ddlType === 19 ||
            [20, 21].includes(ddlType)
          )
        },
        then: (schema) => schema.required(getRequired),
        otherwise: (schema) => schema.nullable(true),
      }),
      answer4: Yup.string().when(['answer1', 'ddl_questiontype'], {
        is: (answer1, ddlType) => (answer1 === '0' && ddlType === 11) || [20, 21].includes(ddlType),
        then: (schema) => schema.required(getRequired),
        otherwise: (schema) => schema.nullable(true),
      }),
      answer5: Yup.string().when(['answer1', 'answer2', 'ddl_questiontype'], {
        is: (answer1, answer2, ddlType) =>
          ((answer1 === '0' || answer2 === '0') && ddlType === 11) || [20, 21].includes(ddlType),
        then: (schema) => schema.required(getRequired),
        otherwise: (schema) => schema.nullable(true),
      }),
      answer6: Yup.string().when(
        ['answer1', 'answer2', 'answer3', 'ddl_questiontype', 'statesLength'],
        {
          is: (answer1, answer2, answer3, ddlType, statesLength) =>
            ((answer1 === '0' || (answer2 === '0' && statesLength > 1) || answer3 === '0') &&
              ddlType === 11) ||
            ddlType === 21,
          then: (schema) => schema.required(getRequired),
          otherwise: (schema) => schema.nullable(true),
        },
      ),
    }),
  statesLength: Yup.number().nullable(true),
  otherwise: (schema) => schema.nullable(true),
})

export const questionsSchema = Yup.object({
  questions: Yup.array()
    .nullable(true)
    .of(
      Yup.object({
        is_skipquestion: Yup.boolean(),
        ddl_questiontype: Yup.number(),
        answer: answerSchema,
        statesLength: Yup.number().nullable(true),

        choice1: Yup.array()
          .nullable(true)
          .when('ddl_questiontype', {
            is: 6,
            then: (schema) =>
              schema.of(
                Yup.object({
                  recordid: Yup.string(),
                  selectedchoice: Yup.string().required(getRequired),
                  childQuestion: Yup.array()
                    .nullable(true)
                    .when(['selectedchoice', 'isChildQuestion'], {
                      is: (val, isChild) => val === 'True' && isChild === true,
                      then: () =>
                        Yup.array().of(
                          Yup.object().shape({
                            is_skipquestion: Yup.boolean(),
                            answer: answerSchema,
                            choice1: choice1Schema,
                          }),
                        ),
                      otherwise: (schema) => schema.nullable(true),
                    }),
                }),
              ),
            otherwise: (schema) =>
              schema.of(
                Yup.object({
                  recordid: Yup.string(),
                  selectedchoice: Yup.string().nullable(true),
                  childQuestion: childQuestionSchema,
                }),
              ),
          }),
        choice2: Yup.array()
          .nullable(true)
          .when('ddl_questiontype', {
            is: 21,
            then: (schema) =>
              schema.of(
                Yup.object({
                  recordid: Yup.string(),
                  selectedchoice: Yup.string().nullable(true),
                  childQuestion: childQuestionSchema,
                }),
              ),
            otherwise: (schema) => schema.nullable(true),
          }),
      }),
    ),
})

export const editClientProfileSchema = Yup.object({
  firstName: required,
  lastName: required,
  nickName: required,
  email: emailValidate(false),
  refferedProfession: required,
  otherProfession: Yup.string().when('refferedProfession', {
    is: (ref) => ref && ref === '4',
    then: () => required,
    otherwise: () => Yup.string().notRequired(),
  }),
})

export const ChangePasswordSchema = (mismatchPassword) => {
  return Yup.object().shape({
    oldPassword: required,
    newPassword: required,
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], mismatchPassword)
      .required(getRequired),
  })
}

export const ChangeUsernameSchema = Yup.object({
  oldUsername: required,
  username: Yup.string().min(8).required(getRequired),
})

export const validationHelplineSchema = Yup.object({
  name: required,
  option: Yup.array().of(Yup.string()).min(1, getRequired),
  mailingAddress: Yup.string().when('option', {
    is: (option) => option && option.includes('mailing'),
    then: () => required,
    otherwise: () => Yup.string().notRequired(),
  }),
  phone: Yup.string().when('option', {
    is: (option) => option && option.includes('phone'),
    then: () => required,
    otherwise: () => Yup.string().notRequired(),
  }),
  email: Yup.string().when('option', {
    is: (option) => option && option.includes('email'),
    then: () => emailValidate(),
    otherwise: () => Yup.string().notRequired(),
  }),
  otherContact: Yup.string().when('option', {
    is: (option) => option && option.includes('other'),
    then: () => required,
    otherwise: () => Yup.string().notRequired(),
  }),
  helplineSource: required,
  helplineSourceOther: Yup.string().when('helplineSource', {
    is: (helplineSource) => helplineSource && (helplineSource === '3' || helplineSource === '4'),
    then: () => required,
    otherwise: () => Yup.string().notRequired(),
  }),
  problemSource: required,
  problemSourceOther: Yup.string().when('problemSource', {
    is: (problemSource) => problemSource && problemSource === '5',
    then: () => required,
    otherwise: () => Yup.string().notRequired(),
  }),
  question: required,
  yesOrno: required,
  otherInfo: Yup.string().when('yesOrno', {
    is: (yesOrno) => yesOrno && yesOrno === 'yes',
    then: () => required,
    otherwise: () => Yup.string().notRequired(),
  }),
  reCaptcha: required,
})

const getLocationValidation = () => ({
  countryKey: required,
  countryName: Yup.string().when('countryKey', {
    is: '0',
    then: () => required,
  }),
  stateKey: Yup.string().when('countryKey', {
    is: (value) => value !== '0',
    then: () => required,
    otherwise: () => Yup.string().notRequired(),
  }),
  stateName: Yup.string().when(['countryKey', 'stateKey'], {
    is: (countryKey, stateKey) => countryKey === '0' || stateKey === '0',
    then: () => required,
  }),
  countyKey: Yup.string().when(['countryKey', 'stateKey', 'statesLength'], {
    is: (countryKey, stateKey, statesLength) =>
      countryKey !== '0' && stateKey !== '0' && statesLength > 1,
    then: () => required,
    otherwise: () => Yup.string().notRequired(),
  }),
  countyName: Yup.string().when(['countryKey', 'stateKey', 'countyKey', 'statesLength'], {
    is: (countryKey, stateKey, countyKey, statesLength) =>
      countryKey === '0' || countyKey === '0' || (stateKey === '0' && statesLength > 1),
    then: () => required,
  }),
})

export const ProfessionalSchema = (myData) => {
  const { txt_mismatchpassword } = myData
  return Yup.object().shape({
    firstName: required,
    lastName: required,
    emailId: emailValidate(),
    phone: Yup.string().required(getRequired),
    username: Yup.string().min(8).required(getRequired),
    password: required,
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], txt_mismatchpassword)
      .required(getRequired),
    isNewsletterSubscribed: Yup.boolean(),
    iAgreeTerms: Yup.boolean().oneOf([true], getRequired),
    reCaptcha: required,
    lawOffice: Yup.string().when('isAttorney', {
      is: (isAttorney) => !isAttorney,
      then: () => required,
      otherwise: () => Yup.string().notRequired(),
    }),
    barAdmissionNo: Yup.string().when('isAttorney', {
      is: (isAttorney) => isAttorney,
      then: () => required,
      otherwise: () => Yup.string().notRequired(),
    }),
    isBarAdmissionGood: Yup.string().when('isAttorney', {
      is: (isAttorney) => isAttorney,
      then: () => required,
      otherwise: () => Yup.string().notRequired(),
    }),
    barAdmissionNotes: Yup.string().when(['isAttorney', 'isBarAdmissionGood'], {
      is: (isAttorney, isBarAdmissionGood) => isAttorney && !isBarAdmissionGood,
      then: () => required,
      otherwise: () => Yup.string().notRequired(),
    }),
    workDescription: Yup.string().when('isAttorney', {
      is: (isAttorney) => !isAttorney,
      then: () => required,
      otherwise: () => Yup.string().notRequired(),
    }),
    ...getLocationValidation(),
  })
}

export const editProfileSchema = (profileType) => {
  return Yup.object().shape({
    firstName: required,
    lastName: required,
    emailId: emailValidate(),
    phone: Yup.string().required(getRequired),
    secondEmail: emailValidate(false),
    thirdEmail: emailValidate(false),
    ...getLocationValidation(),
    workDescription: profileType === 'allied' ? required : undefined,
    barAdmissionNo: profileType === 'attorney' ? required : undefined,
    isBarAdmissionGood:
      profileType === 'attorney' ? Yup.boolean().required(getRequired) : undefined,
  })
}

export const crValidationSchema = (myData) => {
  const { txt_mismatchpassword } = myData
  return Yup.object({
    firstName: required,
    middleName: Yup.string(),
    lastName: required,
    displayName: required,
    emailId: emailValidate(false),
    username: Yup.string().min(8).required(getRequired),
    password: passwordValidate(getRequired),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], txt_mismatchpassword)
      .required(getRequired),
    agreeTerms: Yup.bool().oneOf([true], getRequired),
    subscribeNewsletter: Yup.bool(),
    refferedProfession: required,
    otherProfession: Yup.string().when('refferedProfession', {
      is: (ref) => ref && ref === '4',
      then: () => required,
      otherwise: () => Yup.string().notRequired(),
    }),
    reCaptcha: required,
    intakeTypeId: Yup.number().required(getRequired),
  })
}

export const subscriptionSchema = Yup.object({
  email: emailValidate(),
  subscribedForNewsletter: Yup.boolean(),
  reCaptcha: required,
})

export const AttorneySignupValidation = Yup.object().shape({
  question: Yup.object().shape({
    answer: Yup.object().shape({
      answer1: required,

      answer2: Yup.string().when('answer1', {
        is: (val) => val === '3',
        then: () => required,
      }),
      answer3: Yup.string().when('answer1', {
        is: (val) => val === '4',
        then: () => required,
      }),
      answer4: Yup.string().when('answer1', {
        is: (val) => val === '5',
        then: () => required,
      }),
      answer5: Yup.string().when('answer1', {
        is: (val) => val === '6',
        then: () => required,
      }),
      answer6: Yup.string().when('answer1', {
        is: (val) => val === '8',
        then: () => required,
      }),
      answer7: Yup.string().when('answer4', {
        is: (val) => val === '3',
        then: () => required,
      }),
    }),
  }),
})

export const messageSchema = Yup.object().shape({
  messages: Yup.array().of(
    Yup.object().shape({
      message: required,
    }),
  ),
})

export const ApprovedAttorneySchema = Yup.object()
  .shape({
    siteName: Yup.string(),
    ip: Yup.string(),
    email: Yup.string().email('Invalid email').required('Email is required'),
  })
  .test('siteName-or-ip-required', 'Site Name or IP Address must be provided.', function (values) {
    const { siteName, ip } = values
    return !!(siteName?.trim() || ip?.trim())
  })

export const detentionIPsSchema = Yup.object().shape({
  fromDate: Yup.date().typeError('Invalid Date').required(getRequired),
  toDate: Yup.date().typeError('Invalid Date').required(getRequired),
})
